import { Brand, IdAtendimento, IdInbox } from "../common-types";
import { GenericServiceBase } from "../generic-service";
import { CreateIntegracaoBotIaAgenteDto, FollowUpDTO, UpdateIntegracaoBotIaAgenteDto } from "./integracao-bot-ia-agente.dto";

export class IntegracaoBotIaService extends GenericServiceBase {
    constructor() { super('integracao-bot-ia-endpoints') }

    async vincularAgenteIaToAtendimento(idInbox: IdInbox, numero: string, idContaIa: string | number, idChatIa: string | number) {
        return await this.f.patch(`/bind-bot/inbox/${idInbox}/numero/${numero}/conta/${idContaIa}/chat/${idChatIa}`);
    }

    async desvincularAgenteIaAoAtendimento(idInbox: IdInbox, numero: string, finalizarAtendimento: boolean = false) {
        return await this.f.delete(`/unbind-bot/inbox/${idInbox}/numero/${numero}/finalizar/${finalizarAtendimento}`);
    }

    async checarNumeroTemWpp(idInbox: IdInbox, numero: string) {
        return await this.f.get(`/verificar-wpp/${idInbox}/${numero}`);
    }

    async interromperBotIa(idAtendimento: IdAtendimento, idContaChatBotIa: string, idChatBotIa: string) {
        return await this.f.post(`/interromper-bot-ia/atendimento/${idAtendimento}/idContaChatBotIa/${idContaChatBotIa}/idChatIa/${idChatBotIa}`);
    }

    /**
     * Instâncias vinculáveis, com seus agentes.
     *
     * O filtro de `del` é DEFENSIVO: a fonte (bot-ia) passou a devolver só agentes
     * ativos, mas por um bom tempo devolveu os deletados junto — e o select
     * chegou a oferecer instância/agente excluídos como opção válida, produzindo
     * um vínculo para um recurso que não existe mais. Um ambiente com o bot-ia
     * ainda não atualizado continuaria reproduzindo o bug sem esta linha.
     */
    async consultarInstanciasComAgentes(idInbox: string | null) {
        const instancias = await this.f.get(`/get-instancias-com-agentes/inbox/${idInbox ? idInbox : null}`) as SimplifiedIntegracaoInstancia[];
        return (instancias ?? [])
            .filter(i => !i.del)
            .map(i => ({
                ...i,
                agentes: (i.agentes ?? []).filter(a => !a.del).map(a => this.normalizarAgente(a)),
            }));
    }

    async consultarAgente(idAgente: string, idInbox: string) {
        const agente = await this.f.get(`/agente/${idAgente}/inbox/${idInbox}`) as RawIntegracaoAgente | null;
        return agente ? this.normalizarAgente(agente) : null;
    }

    /**
     * Consumo da quota mensal do Agente (somente leitura).
     *
     * Chamada separada da leitura do Agente de propósito: o consumo muda sozinho a cada conversa
     * aberta, enquanto o Agente é configuração — e no servidor eles têm políticas de cache
     * opostas (o Agente fica em cache por 24 h; o consumo não é cacheado).
     *
     * NUNCA lança. O servidor já responde 200 com `disponivel: false` quando o bot-ia está fora
     * do ar ou ainda não publica a rota; o `catch` aqui cobre o resto (rede, sessão). O painel de
     * quota é informativo — perdê-lo não pode impedir ninguém de editar o prompt do Agente.
     */
    async consultarQuotaMensal(idAgente: string, idInbox: string): Promise<QuotaMensalAgente> {
        try {
            const res = await this.f.get(`/agente/${idAgente}/inbox/${idInbox}/quota-mensal`) as QuotaMensalAgente | null;
            return res ?? { disponivel: false, consumo: null };
        } catch (e) {
            console.warn('[quota-agente] não foi possível ler a quota do Agente:', e);
            return { disponivel: false, consumo: null };
        }
    }

    // O serviço de Bot IA retorna o vínculo da instância como `instanciaId`, mas o restante do
    // front (e o endpoint de criação de agente) usa `instancia`. Normalizamos na entrada para
    // `instancia` e removemos `instanciaId`, evitando que o campo errado seja reenviado no save
    // (o backend valida com forbidNonWhitelisted e rejeitaria `instanciaId`).
    private normalizarAgente(agente: RawIntegracaoAgente): SimplifiedIntegracaoAgente {
        const { instanciaId, _id, ...rest } = agente;
        return {
            ...rest,
            id: agente.id ?? _id ?? ('' as IdIntegracaoAgente),
            // O prompt pode chegar como `promptContexto` (versão nova) ou `prompt` (legado). Ler o
            // campo errado deixaria o formulário em branco e o próximo save APAGARIA o prompt.
            promptContexto: agente.promptContexto ?? agente.prompt ?? '',
            instancia: agente.instancia ?? instanciaId ?? ('' as IdIntegracaoInstancia),
            // Garante objetos preenchíveis para a UI de horário de atendimento, mesmo quando o
            // agente ainda não tem configuração salva (a API pode devolver null/ausente).
            offHoursBehaviour: agente.offHoursBehaviour ?? 'answer_when_day_starts',
            horarioAtendimentoTimezone: agente.horarioAtendimentoTimezone ?? getBrowserTimezone(),
            horarioAtendimento: agente.horarioAtendimento ?? getDefaultHorarioAtendimento(),
            // Contraturno: defaults espelham o backend (modo off, dia desabilitado conta como atendimento).
            modoContraturnoHabilitado: agente.modoContraturnoHabilitado ?? false,
            considerarDiaDesabilitadoComoContraturno: agente.considerarDiaDesabilitadoComoContraturno ?? true,
            // Defaults espelham as colunas do bot-ia: presença e mensagens intermediárias
            // nascem desligadas, reagir nasce LIGADO (com o modelo decidindo o emoji).
            presencaHabilitado: agente.presencaHabilitado ?? false,
            mensagensIntermediariasHabilitado: agente.mensagensIntermediariasHabilitado ?? false,
            reagirHabilitado: agente.reagirHabilitado ?? true,
            reacaoOverride: agente.reacaoOverride ?? null,
            // Um bot-ia anterior a esta feature não devolve `reacaoModo`. Derivá-lo do par
            // legado evita que o formulário abra sem modo selecionado e que o próximo save
            // mande um modo vazio: sem isto, editar o nome do agente derrubaria a reação.
            reacaoModo: agente.reacaoModo
                ?? (agente.reagirHabilitado === false ? 'desabilitado' : (agente.reacaoOverride ? 'fixo' : 'livre')),
            reacaoEmojis: agente.reacaoEmojis
                ?? (agente.reacaoOverride ? [agente.reacaoOverride] : []),
            // Espelha o default da coluna no bot-ia (`@default(true)`). O fallback só entra se a
            // API omitir o campo — um bot-ia anterior à quota. Note que ele nunca INVENTA um
            // `false`: transformar ausência em "parar" desligaria Agentes que ninguém mandou parar.
            continuarAoExcederQuota: agente.continuarAoExcederQuota ?? true,
        };
    }

    async atualizarAgente(idAgente: string, idInbox: string, toUpdate: UpdateIntegracaoBotIaAgenteDto) {
        // Não mutar `toUpdate` no lugar: ele é o estado reativo do formulário, e filtrar os
        // followups in place fazia o form perder followups meio preenchidos durante o save.
        // PRESERVA todos os campos do follow-up — o `.map` anterior projetava para
        // {prompt, tempo}, o que hoje apagaria id, janela, foraDaJanela e o reinício a cada
        // save. Perder o `id` é o pior caso: é a chave que liga o item ao histórico de
        // disparos no bot-ia, e sem ela o follow-up volta a re-disparar.
        const followups = (toUpdate.followups ?? [])
            .filter(f => !!f.prompt && f.prompt.length > 0 && !!f.tempo) as FollowUpDTO[]
        return await this.f.patch(`/agente/${idAgente}/inbox/${idInbox}`, { ...toUpdate, ...mirrorPromptLegado(toUpdate), followups });
    }

    async criarAgente(idInbox: string, toCreate: CreateIntegracaoBotIaAgenteDto) {
        // O endpoint de criação pode devolver o identificador como `id` ou `_id` (mongo). Normaliza
        // para `_id` (formato que o chamador consome), evitando que o id recém-criado se perca e que
        // o próximo save acabe criando outro agente em vez de atualizar.
        const created = await this.f.post(`/agente/inbox/${idInbox}`, { ...toCreate, ...mirrorPromptLegado(toCreate) }) as { id?: IdIntegracaoAgente, _id?: IdIntegracaoAgente };
        return { _id: created._id ?? created.id ?? ('' as IdIntegracaoAgente) };
    }

    async toggleAgenteAtivo(idAgente: string, idInbox: string) {
        return await this.f.patch(`/toggle-active/agente/${idAgente}/inbox/${idInbox}`) as SimplifiedIntegracaoAgente;
    }

    getDummyIntegracaoAgente(idInstancia: IdIntegracaoInstancia) {
        return {
            id       : '' as IdIntegracaoAgente,
            nome     : '' as string,
            promptContexto      : '' as string,
            promptTomDeVoz      : null as string | null,
            promptObjetivo      : null as string | null,
            promptComandoDeSaida: null as string | null,
            instancia: idInstancia,

            agenteSemComandoInicio: false,
            comandoIniciar       : '',
            
            agenteFollowupHabilitado: false,
            horaInicioAgenteFollowup: '08:00',
            horaFimAgenteFollowup   : '18:00',
            followups               : [] as SimplifiedIntegracaoAgenteFollowUp[],
            integrarComCanais       : false,

            horarioAtendimentoHabilitado: false,
            offHoursBehaviour           : 'answer_when_day_starts' as OffHoursBehaviour,
            horarioAtendimentoTimezone  : getBrowserTimezone(),
            horarioAtendimento          : getDefaultHorarioAtendimento(),

            modoContraturnoHabilitado               : false,
            considerarDiaDesabilitadoComoContraturno: true,

            presencaHabilitado               : false,
            mensagensIntermediariasHabilitado: false,
            reagirHabilitado                 : true,
            reacaoOverride                   : null as string | null,

            // Agente novo nasce como o bot-ia o cria: seguindo atendendo ao exceder a quota.
            continuarAoExcederQuota          : true,
        } as SimplifiedIntegracaoAgente
    }

}

// Fuso do navegador como padrão; cai para America/Sao_Paulo se a API não estiver disponível.
export function getBrowserTimezone(): string {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo' }
    catch { return 'America/Sao_Paulo' }
}

// Janela padrão: seg–sex 08:00–18:00 habilitados; dom e sáb desabilitados.
export function getDefaultHorarioAtendimento(): HorarioAtendimentoAgente {
    const uteis = { Enabled: true, Inicio: '08:00', Fim: '18:00' };
    const fds = { Enabled: false, Inicio: '08:00', Fim: '18:00' };
    const build = (d: DiaHorarioAtendimento, v: typeof uteis) => ({
        [`${d}Enabled`]: v.Enabled, [`${d}Inicio`]: v.Inicio, [`${d}Fim`]: v.Fim,
    });
    return {
        ...build('dom', fds), ...build('seg', uteis), ...build('ter', uteis),
        ...build('qua', uteis), ...build('qui', uteis), ...build('sex', uteis),
        ...build('sab', fds),
    };
}

/**
 * Instância de bot de IA como o whiz a consome.
 *
 * Os campos abaixo de `agentes` SEMPRE vieram no payload de
 * `/instancias/integracao/all` (a rota devolve o registro completo do bot-ia) —
 * só não estavam declarados aqui, e por isso o select de vínculo mostrava apenas
 * o nome. São opcionais porque nenhum deles é garantido: dependem do último
 * refresh de status/foto no bot-ia.
 */
export type SimplifiedIntegracaoInstancia = {
    id: IdIntegracaoInstancia,
    nome: string,
    type: SimplifiedIntegracaoInstanciaType,
    agentes: SimplifiedIntegracaoAgente[],

    /** Soft-delete no bot-ia. Instâncias deletadas não podem ser vinculadas. */
    del?: boolean,
    /** "open" quando conectada; qualquer outro valor ("close", "connecting"...) = fora do ar. */
    status?: string | null,
    /** Nome do perfil do WhatsApp conectado. */
    profileName?: string | null,
    /** Número conectado, cru (só dígitos). Formatado para exibição no card. */
    connectedNumber?: string | null,
    /** URL "ao vivo" da foto de perfil (absoluta, servida pelo provider). */
    profilePicUrl?: string | null,
    /**
     * Caminho RELATIVO da cópia persistida da foto no bot-ia (ex.: "/storage/x.jpg").
     * O whiz não conhece a base do bot-ia, então só é usado quando vier absoluto.
     */
    profilePicStoredPath?: string | null,
    createdAt?: string | null,
    updatedAt?: string | null,
    /**
     * Vínculo da Instância com o whiz. `plataforma` é a plataforma do inbox para o qual a
     * Instância foi criada — é o que permite não oferecer uma Instância de WhatsApp para um
     * inbox do Instagram. Sempre veio no payload de `/instancias/integracao/all` (a rota
     * devolve o registro completo, com `whizV2Info` incluído); só não estava declarado aqui.
     */
    whizV2Info?: {
        idInbox?: string | null,
        idConta?: string | null,
        env?: string | null,
        plataforma?: string | null,
    } | null,
}
export type SimplifiedIntegracaoAgente = { 
    id: IdIntegracaoAgente, 
    nome: string, 
    // Prompt em 4 seções (ordem no modelo: tom de voz → objetivo → contexto → comando de saída;
    // vazias são puladas). `promptContexto` é o antigo campo único `prompt`.
    promptContexto: string, 
    promptTomDeVoz?: string | null, 
    promptObjetivo?: string | null, 
    promptComandoDeSaida?: string | null, 
    instancia: IdIntegracaoInstancia, 
    agenteSemComandoInicio: 
    boolean, 
    comandoIniciar?: string, 
    agenteFollowupHabilitado: boolean, 
    horaInicioAgenteFollowup: HorarioHHMM,
    horaFimAgenteFollowup: HorarioHHMM,
    followups: SimplifiedIntegracaoAgenteFollowUp[],
    integrarComCanais: boolean,
    disabled: boolean,

    // Horário de atendimento do Agente (wall-clock no fuso `horarioAtendimentoTimezone`).
    horarioAtendimentoHabilitado?: boolean,
    offHoursBehaviour?: OffHoursBehaviour | null,
    horarioAtendimentoTimezone?: string | null,
    horarioAtendimento?: HorarioAtendimentoAgente | null,

    // Contraturno: inverte o sentido da janela (passa a ser o horário evitado).
    modoContraturnoHabilitado?: boolean,
    considerarDiaDesabilitadoComoContraturno?: boolean,

    // Presença ("digitando..."), fala durante o uso de ferramentas e reação com emoji.
    // Configurados no bot-ia; editáveis também por aqui desde 2026-08.
    presencaHabilitado?: boolean,
    mensagensIntermediariasHabilitado?: boolean,
    /**
     * Modo de reação: 'desabilitado' | 'livre' | 'fixo' | 'lista'. Substitui o par legado
     * abaixo, que o bot-ia continua derivando por dual-write para clientes não migrados.
     */
    reacaoModo?: string,
    /** Emojis da reação: vazio em desabilitado/livre; 1 em fixo; 2+ distintos em lista. */
    reacaoEmojis?: string[],
    /** LEGADO — derivado de `reacaoModo` pelo bot-ia. Não escreva nele. */
    reagirHabilitado?: boolean,
    /** LEGADO — `null` = o modelo decide. Preenchido = sempre esse emoji. */
    reacaoOverride?: string | null,

    /**
     * Ao ESTOURAR a quota mensal de Chats: seguir atendendo (padrão) ou parar de abrir conversas
     * novas até a competência virar. É a única parte da quota que o cliente controla — o TETO
     * (`quotaMensalChats`) é decisão de plataforma e nem chega até aqui.
     */
    continuarAoExcederQuota?: boolean,

    // --- Campos já presentes no payload do bot-ia, usados pelo card do select ---
    /** Soft-delete no bot-ia. Agentes deletados não podem ser vinculados. */
    del?: boolean,
    /**
     * Provedor de IA ("openai", "deepseek", ...) e modelo configurados no agente.
     * Não são anuláveis no bot-ia (colunas obrigatórias) — manter `string` aqui é o
     * que permite reaproveitar o objeto direto nos DTOs de criar/atualizar agente.
     */
    aiProvider?: string,
    aiModel?: string,
    createdAt?: string | null,
    updatedAt?: string | null,
}
/**
 * Um follow-up do Agente, como o whiz o lê e reenvia.
 *
 * Os campos de configuração precisam trafegar INTEIROS: o modal salva o agente completo num
 * PATCH, e qualquer projeção no caminho (aqui ou no DTO do servidor) apaga a configuração
 * feita no painel do bot-ia. `id` é o mais sensível — liga o item ao histórico de disparos.
 */
export type SimplifiedIntegracaoAgenteFollowUp = {
    id?: string,
    nome?: string | null,
    prompt: string,
    tempo: number,
    /** 'sempre' | 'followup_hours' | 'horario_atendimento' */
    janela?: string,
    /** 'adiar' | 'pular' */
    foraDaJanela?: string,
    reiniciaComRespostaDoCliente?: boolean,
    maxDisparosPorChat?: number | null,
    /** Dispara mesmo com o chat na janela de espera do encerramento decidido pela IA. */
    disparaEmEncerramentoPendente?: boolean,
}

export type OffHoursBehaviour = 'answer_when_day_starts' | 'ignore_incoming_on_off_hours'

// Janela de atendimento por dia da semana (dom..sab). Horários "HH:mm" são wall-clock no fuso
// do Agente — enviados/recebidos verbatim, SEM conversão de timezone no front.
export type DiaHorarioAtendimento = 'dom' | 'seg' | 'ter' | 'qua' | 'qui' | 'sex' | 'sab'
export type HorarioAtendimentoAgente = {
    [K in `${DiaHorarioAtendimento}Enabled`]?: boolean
} & {
    [K in `${DiaHorarioAtendimento}Inicio` | `${DiaHorarioAtendimento}Fim`]?: string | null
}

// Shape cru retornado pelo serviço de Bot IA: o identificador pode vir como `_id` (mongo) em vez
// de `id`, e o vínculo da instância como `instanciaId` em vez de `instancia`.
/**
 * Consumo da quota mensal do Agente, como o whiz-server o entrega.
 *
 * `disponivel: false` não é erro: é o bot-ia fora do ar, ou ainda numa versão anterior à que
 * publica a rota. Nesse caso o painel de quota simplesmente não aparece, e o resto do formulário
 * do Agente continua editável.
 */
export type QuotaMensalAgente = {
    disponivel: boolean,
    consumo: ConsumoQuotaMensalAgente | null,
}

/**
 * Espelha `ConsumoQuotaMensal` do bot-ia.
 *
 * `esgotada`, `continuarAoExcederQuota` e `bloqueado` NÃO são sinônimos e não podem ser
 * colapsados: `esgotada` é "o teto foi alcançado" (segue true enquanto o Agente atende acima
 * dele), e `bloqueado` é `esgotada && !continuarAoExcederQuota` — o único que tira o Agente do
 * roteamento. Com um booleano só, ligar "continuar" apagaria da tela a informação de que o teto
 * estourou.
 */
export type ConsumoQuotaMensalAgente = {
    /** Competência YYYY-MM, resolvida no fuso do Agente. */
    competencia: string,
    usados: number,
    /** O teto vigente. NUNCA nulo: o do Agente, ou o padrão global herdado. */
    teto: number,
    /** true quando o teto é o padrão global herdado, e não um valor próprio do Agente. */
    tetoHerdado: boolean,
    /** O teto foi ALCANÇADO. Continua true enquanto o Agente atende acima dele. */
    esgotada: boolean,
    esgotadaEm: string | null,
    continuarAoExcederQuota: boolean,
    /** Chats desta competência que nasceram acima do teto. */
    acimaDaQuotaChats: number,
    /** O Agente PAROU de abrir conversas novas. É `esgotada && !continuarAoExcederQuota`. */
    bloqueado: boolean,
}

export type RawIntegracaoAgente = Omit<SimplifiedIntegracaoAgente, 'id' | 'instancia' | 'promptContexto'> & {
    id?: IdIntegracaoAgente,
    _id?: IdIntegracaoAgente,
    instancia?: IdIntegracaoInstancia,
    instanciaId?: IdIntegracaoInstancia,
    // O prompt pode chegar como `promptContexto` (versão nova) ou `prompt` (legado). O whiz-server
    // já espelha os dois, mas o front normaliza também — é o que impede que um save subsequente
    // grave o contexto VAZIO caso alguma ponta ainda não esteja atualizada.
    promptContexto?: string,
    prompt?: string,
}

/**
 * Espelha `promptContexto` no alias legado `prompt` ao SALVAR.
 *
 * Um whiz-server ainda não atualizado exige `prompt` (obrigatório no DTO antigo) e não conhece
 * `promptContexto`; um atualizado prefere `promptContexto` e aceita o alias. Mandar os dois é o que
 * faz o save funcionar independentemente de qual versão está no ar. Quando todos os ambientes
 * estiverem atualizados, o espelho sai daqui e o campo sai do DTO.
 */
export function mirrorPromptLegado(agente: { promptContexto?: string | null }): { prompt: string } {
    return { prompt: agente.promptContexto?.trim() ?? '' };
}

/**
 * Tipos de Instância do bot-ia. Só `WHIZV2` nasce de um inbox do whiz v2 (e por isso é a
 * única que traz `whizV2Info`); as demais apontam para um transporte próprio do bot-ia.
 * `WHATSMEOW-STAGING` já chegava no payload e era tratado em `rotuloTipoInstancia` — só
 * faltava aqui.
 */
export type SimplifiedIntegracaoInstanciaType = 'DIRECT-EVOLUTION' | 'GATED-EVOLUTION' | 'WHIZV1-EVOLUTION' | 'WHIZV1-ZAPI' | 'WHATSMEOW-STAGING' | 'WHIZV2'
export type IdIntegracaoInstancia = Brand<string, 'IdIntegracaoInstancia'>
export type IdIntegracaoAgente = Brand<string, 'IdIntegracaoAgente'>
export type HorarioHHMM = Brand<string, 'HorarioHHMM'>

